import { clsx, type ClassValue } from 'clsx'


export function cn(...inputs: ClassValue[]): string {
    return clsx(inputs)
}

export function debounce<T extends (...args: unknown[]) => void>(
    func: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), delay)
    }
}


export const _sleep = (delay = 1000): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, delay))


export const getInitials = (name: string): string =>
    name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2)

interface UserData {
    firstName?: string
    lastName?: string
}

export const nameConvertor = (userData: UserData | null = null): string =>
    [userData?.firstName, userData?.lastName].filter(Boolean).join(' ')

export function hasNoHtml(str: string): boolean {
    return !/<\/?[^>]+>/g.test(str)
}

export function replaceTemplate(str: string, data: Record<string, string>): string {
    return str.replace(/{{(.*?)}}/g, (_match, key: string) => data[key.trim()] ?? '')
}


interface FormatAmountOptions {
    style?: Intl.NumberFormatOptions['style']
    currency?: string | null
}

export const formatInternationalAmount = (
    amount = 0,
    options: FormatAmountOptions = { style: 'decimal', currency: null },
    maximumFractionDigits = 0
): string => {
    const { style = 'decimal', currency } = options
    const formatOptions: Intl.NumberFormatOptions = { style, maximumFractionDigits }
    if (currency) formatOptions.currency = currency
    return new Intl.NumberFormat('en-US', formatOptions).format(amount)
}


export const TimeStemp = (): number => Date.now()

/** Returns "Last Monday, 3:45 PM" style string */
export const convetIntoLastFormat = (date: string | number | Date): string => {
    const d = new Date(date)
    return d.toLocaleString('en-US', {
        weekday: 'long',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).replace(/^/, 'Last ')
}

/** "2m", "3h", "5d", "12 Jan" relative time without external deps */
export const shortFromNow = (time: string | number | null | undefined): string => {
    if (!time) return '-'

    const ts =
        typeof time === 'number' && time.toString().length === 10 ? time * 1000 : Number(time)

    const d = new Date(ts)
    if (isNaN(d.getTime())) return '-'

    const diffSeconds = Math.floor((Date.now() - d.getTime()) / 1000)

    if (diffSeconds < 60) return `${diffSeconds}s`
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m`
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h`
    if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d`

    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

/** Format a Date for <input type="datetime-local"> */
export function formatForDateTimeLocal(date: string | number | Date): string {
    const d = new Date(date)
    const pad = (n: number) => String(n).padStart(2, '0')
    return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    )
}

/** Strip timezone from ISO string for datetime-local inputs */
export function toDateTimeLocalNoTZ(isoString: string): string {
    const [date, time] = isoString.split('T')
    const [hours, minutes] = time.split(':')
    return `${date}T${hours}:${minutes}`
}

/** "+05:30" UTC offset string from total minutes */
export function createTimeUtcOffset(minutes: number): string {
    return (
        `+${String(Math.floor(minutes / 60)).padStart(2, '0')}:` +
        `${String(minutes % 60).padStart(2, '0')}`
    )
}

// ─── Status chip ──────────────────────────────────────────────────────────────

export function getStatusChipClasses(status: string | null | undefined): string {
    const value = String(status ?? '').toLowerCase()
    if (value === 'in_progress' || value === 'queued') return 'bg-yellow-500'
    if (value === 'failed') return 'bg-red-500'
    if (value === 'succeeded') return 'bg-green-500'
    if (value === 'running') return 'bg-blue-500'
    return 'bg-gray-400'
}

// ─── Deep equality ────────────────────────────────────────────────────────────

export function deepEquals(a: unknown, b: unknown): boolean {
    if (a === b) return true
    if (a == null || b == null) return false

    if (a instanceof Date && b instanceof Date)
        return a.getTime() === b.getTime()

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false
        return a.every((val, i) => deepEquals(val, b[i]))
    }

    if (typeof a === 'object' && typeof b === 'object') {
        const keysA = Object.keys(a as object)
        const keysB = Object.keys(b as object)
        if (keysA.length !== keysB.length) return false
        return keysA.every(key =>
            deepEquals((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
        )
    }

    return false
}

// ─── File conversion ──────────────────────────────────────────────────────────

interface ConvertedFile {
    fileName: string
    mimeType: string
    fileSize: number
    fileBuffer: Uint8Array
}

const ALLOWED_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
])

const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf', '.docx', '.doc']

export const convertFiles = async (files: FileList | File[]): Promise<ConvertedFile[]> => {
    const filePromises = Array.from(files)
        .filter(file => {
            const nameLower = file.name.toLowerCase()
            const allowed =
                ALLOWED_MIME_TYPES.has(file.type) ||
                ALLOWED_EXTENSIONS.some(ext => nameLower.endsWith(ext))
            if (!allowed) console.warn(`File rejected: ${file.name} (type: ${file.type})`)
            return allowed
        })
        .map(file => {
            if (file.size > 1_000_000) {
                console.warn(`Warning: ${file.name} is ${file.size} bytes — may exceed 1 MB Socket.IO limit`)
            }

            return new Promise<ConvertedFile>((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsArrayBuffer(file)
                reader.onload = () =>
                    resolve({
                        fileName: file.name,
                        mimeType: file.type || 'application/octet-stream',
                        fileSize: file.size,
                        fileBuffer: new Uint8Array(reader.result as ArrayBuffer),
                    })
                reader.onerror = reject
            })
        })

    return Promise.all(filePromises)
}
